import { Component, Inject, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, ValidatorFn } from '@angular/forms';
import { Company, Invitation, Profile, App, UserApp } from 'app/models';
import { Observable, Subject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { AppsService } from 'app/services';
import { environment } from '../../../../../environments/environment';
import { MatStepper } from '@angular/material/stepper';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent} from '@angular/material/chips';
import { TranslateService } from '@ngx-translate/core';
import { GenericRoles } from 'app/helpers/constants';

interface CompanyData {
  title: string;
  message: string;
  branches: Company[];
  users: Profile[];
  invitations: Invitation[];
  branchAssignments: FormControl;
  email: FormControl;
  emailExists: ValidatorFn;
  apps: Array<UserApp>;
  toggleDirectToBranch(value: boolean): void;
  toggleAdmin(value: boolean): void;
  setApps(arg0: UserApp[]);
}


@Component({
  standalone: false,
  selector: 'app-invite-user-dialog',
  templateUrl: './invite-user-dialog.component.html',
  styleUrls: ['./invite-user-dialog.component.scss'],
})
export class InviteUserDialogComponent implements OnInit, OnDestroy {


  branchCtrl = new FormControl('');
  @ViewChild('branchInput') branchInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autoTrigger: MatAutocompleteTrigger;
  searchControl = new FormControl();
  @ViewChild('myInput') input: ElementRef<HTMLInputElement>;
  public branchAssignments = new FormControl();
  public email: FormControl;
  selectable = true;
  removable = true;
  public emailExists: ValidatorFn;
  public apps: Array<UserApp> = new Array();
  public selectedApp: App;
  isAdmin = false;
  inviteDirectlyToBranch = false;
  private onDestroy$ = new Subject<void>();
  v3Flag: boolean = false;
  allBranches: any[];
  filteredBranches: Observable<any[]>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CompanyData,
    private appsService: AppsService,
    public dialogRef: MatDialogRef<InviteUserDialogComponent>,
    private _translateService: TranslateService
  ) {}



  ngOnInit() {
    this.allBranches = this.data.branches;
    this.data.branchAssignments.setValue([]);
    this.data.email.reset();
    this.getApps();

    this.filteredBranches = this.branchCtrl.valueChanges.pipe(
      startWith(null),
      map((searchValue: string | null) =>
      (typeof searchValue === 'string' ? this._filter(searchValue, this.data.branches) : this.allBranches.slice())),
    );
    
    this.data.email.statusChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.data.email.markAsTouched();
    });
  }

  checkBoxChange(event,branch){
    //reset toggle for isBranchAdmin before showing it
    branch.isBranchAdmin = false;
    if(event.checked){
      const branchToAdd = [...this.data.branchAssignments.value, branch];
      this.data.branchAssignments.setValue(branchToAdd)
    }
  else{
    const branchToRemove = this.data.branchAssignments.value.filter(b=>b.accountId !== branch.accountId)
    this.data.branchAssignments.setValue(branchToRemove)
}

  }
  selectAllBranches(event){
    if(event.checked){
      this.filteredBranches.pipe(take(1)).subscribe((branches)=>
      {
        branches.forEach(branch => {
        branch.isBranchAdmin = false
      });
        this.data.branchAssignments.setValue([...branches.filter(b=>b.status==='Active')])
    });
    }
    else{
      this.data.branchAssignments.setValue([]);
    }
  }
  checkPartialSelection(){
    let isPartialSelection= false;
    if(!this.checkAllSelected()){
      this.filteredBranches.pipe(map(branches=>
        branches.filter(b=>b.status==='Active').some(branch=>this.data.branchAssignments.value.some(branchAssignment=>branchAssignment.accountId === branch.accountId))
      )).subscribe(res=>isPartialSelection=res)
    }
    return isPartialSelection;
  }
  
  checkAllSelected(){
    let isAllBrnachesSelected= false;
    this.filteredBranches.pipe(map(branches=>
      branches.filter(b=>b.status==='Active').every(branch=>this.data.branchAssignments.value.some(branchAssignment=>branchAssignment.accountId === branch.accountId))
    )).subscribe(res=>isAllBrnachesSelected=res)
    return isAllBrnachesSelected;
  }

  focusOut(){
    this.branchInput.nativeElement.value = '';
    this.branchCtrl.setValue(null);
  }

  checkSelected(branch){
    const index = this.data.branchAssignments.value.indexOf(branch);
    return index >= 0;
  }

  removeBranch(branch): void {
    const branchToRemove = this.data.branchAssignments.value.filter(b=>b.accountId !== branch.accountId)
    this.data.branchAssignments.setValue(branchToRemove)
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const index = this.data.branchAssignments.value.indexOf(event.option.value);
    let branch;
    if(index>=0){
      branch = this.data.branchAssignments.value.filter(b=>b.accountId !== event.option.value.accountId)
    }

    else{
    branch = [...this.data.branchAssignments.value, event.option.value];
    }

    this.data.branchAssignments.setValue(branch)
    this.branchInput.nativeElement.value = '';
    this.branchCtrl.setValue(null);
  }

  private _filter(value: string, branches: any[]): any[] {
    const filterValue = value.toLowerCase();
    return branches.filter((branch) => {
      const branchProperties = Object.values(branch);
      return branchProperties.some((property) => {
        if (typeof property === 'string') {
          return property.toLowerCase().includes(filterValue);
        } else if (typeof property === 'number') {
          return property.toString().includes(filterValue);
        }
        return false;
      });
    });
  }

  getApps() {
    this.appsService.apps$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((apps) => {
        apps = apps.filter(
          (app) =>
            app.clientId !== environment.alleAuthConfig.clientID &&
            !app.clientMetadata?.internalOnly &&
            app.isUserAdmin &&
            app.roles &&
            app.roles.length
        );
        apps = apps.sort((a, b) => a.name.localeCompare(b.name));
        this.apps = apps;
        this.setToggleState();
      });
  }

  getV3Apps(): void {
    this.v3Flag = true;
    this.appsService.getApplications();
    this.appsService.applicationRoles$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((apps) => {
        this.apps = apps.filter(app => {
          return app.clientId !== environment.alleAuthConfig.clientID &&
            !app.clientMetadata?.internalOnly &&
            app.roles &&
            app.roles.length;
        }).sort((a, b) => a.name.localeCompare(b.name));
        this.setToggleState();      
      }, error => {
        console.error('Error fetching applications:', error);
      });
  }

  toggleAdmin(value: boolean): void {
    this.isAdmin = value;
    this.data.toggleAdmin(value);
    this.data.branchAssignments.value.forEach((ba) => {
      this.toggleBranchAdmin(ba, false);
    })
  }

  toggleDirectToBranch(value: boolean): void {
    this.inviteDirectlyToBranch = value;
    this.data.toggleDirectToBranch(value);
  }

  add(event: MatChipInputEvent): void {

    if (this.data.email.invalid) {
      this.data.email.markAsTouched();
      return;
    }
    this.toggleAdmin(false);
    this.data.email.reset();
  }

  checkEmail() {
    return (
      (this.data.email.value && this.data.email.invalid) ||
      (this.data.email.value &&
        this.data.email.value.includes('@allegion.com')) ||
      !this.data.email.value
    );
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.unsubscribe();
  }

  goBack(stepper: MatStepper){
  stepper.selected.completed = false;
  stepper.previous();
}

goForward(stepper: MatStepper){
  stepper.selected.completed = true;
  stepper.selected.editable = true;
    stepper.next();
}
  reset(selectedApp, event) {
    selectedApp.toggleState = event.checked;
    if (!event.checked) {
      selectedApp.roles.forEach((role) => {
        role.hasAccess = false;
      });
    } else {
      selectedApp.roles.forEach((role) => {
        if (role.name.toLowerCase() === GenericRoles.userRole.toLowerCase()) {
          role.hasAccess = true;
        }
      });
    }
  }

  setToggleState() {
    this.apps.forEach((app) => {
      app.roles = app.roles.filter((r) => r.name.toLowerCase() !== GenericRoles.appManagerRole.toLowerCase());
      app.roles.forEach(appRoles => appRoles.hasAccess = false);
      app.toggleState = false;
      const role = app.roles.find((r) => r.name.toLowerCase() === GenericRoles.userRole.toLowerCase());
      if (role) {
        app.hasUserRole = true;
      } else {
        app.hasUserRole = false;
      }
    });
  }

  save() {
    this.data.setApps(this.apps.filter((app) => app.toggleState));
    this.dialogRef.close(true);
  }

  cancel() {
    this.data.branchAssignments.value.forEach((ba) => {
      this.toggleBranchAdmin(ba, false);
    })
    this.dialogRef.close(false);
  }

  getAppRoles(app) {
    return app.roles
      .filter((role) => role.hasAccess)
      .map((role) => role.name)
      .join(', ');
  }
  getBranchNames() {
   
    if (!this.data.branchAssignments.value) {
      return '';
    }

    let sectorNumber='';
    let headquarters ='';
    this._translateService
        .get('INVITE.sector_no')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (sectorNumber = value));
      this._translateService
        .get('COMPANY_BRANCHES.headQuarters')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (headquarters = value));
    return this.data.branchAssignments.value.map((ba) =>
    (ba.parentAccountId)? ba.name +' - '+sectorNumber + ba.sectorNumber +' '+ ba.city + ',' + ba.state+(ba.isBranchAdmin? ' : Branch Admin':''): ba.name +' - '+sectorNumber + ba.sectorNumber+' '+ba.city + ',' + ba.state+ '  ('+headquarters+')').join('\n')
  }

  toggleBranchAdmin(selectedBranch, toggleState){
    selectedBranch.isBranchAdmin = toggleState;
  }

  isEqual(value1 : string, value2: string) : boolean{
    if(!value1 || !value2) return false;
    return value1.toLocaleLowerCase() === value2.toLocaleLowerCase();
  }

  hasSelectedBranch(): boolean {
    return this.data.branchAssignments.value.length > 0 ;
  }

  allToggledAppsHaveRoles(): boolean {
    if (!this.apps || this.apps.length === 0) {
      return true; 
    }
    const toggledApps = this.apps.filter(app => app.toggleState);
    if (toggledApps.length === 0) {
      return true; 
    }
    return toggledApps.every(app => Array.isArray(app.roles) && app.roles.some(r => r.hasAccess));
  }
}
